import { Link, useNavigate } from "react-router-dom";
import { usePlaces, placeFor, placeUrl, useRegions, regionFor, regionUrl } from "@/lib/places";
import { useLang, useSiteSettings } from "@/context/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { NewBadge } from "@/components/NewBadge";
import { CardGallery } from "@/components/CardGallery";
import { Star, MapPin, ArrowRight } from "lucide-react";
import { weekFrom, nightFrom } from "@/lib/pricing";
import { HeartButton } from "@/components/HeartButton";
import { PropertyFacts } from "@/components/PropertyFacts";
import { propertyUrl, tr } from "@/lib/translations";
import { useOffers, offersFor, effectiveOffer, offerDetailLabel } from "@/lib/offers";

export default function PropertyCard({ property, size = "large", layout = "tile", query = "" }) {
  const { lang, t } = useLang();
  const site = useSiteSettings();
  const row = layout === "row";
  const amenityLimit = size === "small" ? site.amenities_show_tile_small : site.amenities_show_tile_large;
  const cardOffers = offersFor(useOffers(), property).filter((o) => o.kind !== "last_minute" || !(property.last_minute_percent > 0));
  const title = tr(property.short_title, lang) || tr(property.title, lang);
  const teaser = tr(property.teaser, lang);
  const navigate = useNavigate();
  const url = propertyUrl(property) + (query ? `?${query}` : "");
  const pp = property.period_price && property.period_price.nights > 0 ? property.period_price : null;
  const place = placeFor(usePlaces(), property);
  const region = regionFor(useRegions(), property);
  // Karte als Ganzes klickbar; echte Links (Titel, „Zum Objekt“, Ort) bleiben für Google und Tastatur erhalten
  const open = (e) => { if (e.target.closest("a,button")) return; navigate(url); };

  return (
    <article
      onClick={open}
      className={`group cursor-pointer bg-white rounded-2xl overflow-hidden min-w-0 border border-stone-200/60 card-hover ${row ? "flex flex-col sm:flex-row" : "block"}`}
      data-testid={`property-card-${property.slug}`}
      data-layout={layout}
    >
      <div className={`relative aspect-[4/3] overflow-hidden img-hover-zoom ${row ? "sm:w-80 lg:w-[370px] shrink-0 sm:aspect-auto sm:min-h-[260px]" : ""}`}>
        <CardGallery property={property} title={title} lang={lang} />
        <HeartButton property={property} className="absolute top-3 right-3 z-10" />
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          <NewBadge property={property} />
          {property.last_minute_percent > 0 && (
            <Badge className="bg-badge-offer hover:bg-badge-offer text-white text-[10px] uppercase tracking-wider" data-testid="last-minute-badge">
              −{property.last_minute_percent} % {t.property.lastMinute} · {t.property.daysLeft.replace("{days}", property.last_minute_days)}
            </Badge>
          )}
          {site.offers_show_badge !== false && cardOffers.slice(0, property.last_minute_percent > 0 ? 1 : 2).map((o) => (
            <Badge key={o.id} className="bg-badge-offer hover:bg-badge-offer text-white text-[10px] uppercase tracking-wider" data-testid={`offer-badge-${o.id}`}>
              {offerDetailLabel(effectiveOffer(o, property), t, lang)}
            </Badge>
          ))}
          {property.instant_book && (
            <Badge className="bg-badge-instant hover:bg-badge-instant text-white text-[10px] uppercase tracking-wider">
              {t.property.instantBook}
            </Badge>
          )}
        </div>
        <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-sm font-semibold" data-testid="card-price-badge">
          {pp
            ? <><span className="text-xs text-stone-500 font-normal">{pp.nights} {pp.nights === 1 ? t.property.night : t.prices.nights} </span>€{Math.round(pp.total).toLocaleString("de-DE")}</>
            : property.longterm_only && property.monthly_price > 0
              ? <><span className="text-xs text-stone-500 font-normal">{t.property.priceFrom} </span>€{Math.round(property.monthly_price).toLocaleString("de-DE")}<span className="text-xs text-stone-500 font-normal"> / {t.property.month}</span></>
              : <><span className="text-xs text-stone-500 font-normal">{t.property.priceFrom} </span>€{nightFrom(property)}<span className="text-xs text-stone-500 font-normal"> / {t.property.night}</span></>}
        </div>
      </div>

      <div className={`${row ? "p-4 flex-1 min-w-0 flex flex-col" : "p-5"}`}>
        <div className="flex items-center gap-1 text-xs text-stone-500 mb-1">
          {property.ref_number && <span className="font-semibold text-signal whitespace-nowrap" data-testid="card-ref-number">Nr. {property.ref_number}</span>}
          <MapPin className="w-3 h-3 shrink-0 ml-1" />
          {place
            ? <Link to={placeUrl(place, lang)} className="truncate hover:text-brand hover:underline underline-offset-2 inline-block py-1" title={t.places.link.replace("{n}", place.name)} data-testid="card-place-link">{property.location}</Link>
            : <span className="truncate">{property.location}</span>}
          {region && <span className="truncate whitespace-nowrap" data-testid="card-region"><span className="mx-1 text-stone-300">·</span><Link to={regionUrl(region, lang)} className="hover:text-brand hover:underline underline-offset-2 inline-block py-1" data-testid="card-region-link">{tr(region.name, lang)}</Link></span>}
        </div>
        <h3 className={`font-serif leading-snug text-stone-900 group-hover:text-brand transition-colors ${row ? "text-xl md:text-2xl" : "text-lg truncate"}`} title={title} data-testid="card-title">
          <Link to={url} className="focus-visible:outline-none focus-visible:underline" data-testid="card-title-link">{title}</Link>
        </h3>
        {teaser && <p className={`text-sm text-stone-600 ${row ? "mt-1.5 line-clamp-2" : "mt-1 line-clamp-3"}`} data-testid="card-teaser">{teaser}</p>}
        {row && <PropertyFacts property={property} limit={site.amenities_show_rows ?? 5} className="mt-4 pt-3 border-t border-stone-100 text-sm text-stone-600" testId={`card-row-facts-${property.slug}`} />}
        <div className={`flex items-center gap-4 text-sm text-stone-600 ${row ? "mt-auto pt-2" : "mt-3"}`}>
          {!row && <PropertyFacts property={property} limit={amenityLimit} iconOnly testId={`card-facts-${property.slug}`} />}
          <span className="flex items-center gap-1 ml-auto text-gold">
            <Star className="w-4 h-4 fill-current" /> {property.rating}
          </span>
        </div>
        <div className={`${row ? "mt-2 pt-2" : "mt-3 pt-3"} border-t border-line text-xs text-stone-600 flex items-center justify-between gap-3`} data-testid="card-week-price">
          {pp
            ? <span data-testid="card-period-price">
                <span className="font-semibold text-brand">{t.property.periodTotal.replace("{n}", pp.nights).replace("{price}", Math.round(pp.total).toLocaleString("de-DE"))}</span>
                {pp.discount > 0 && <span className="ml-1.5 text-red-600 font-semibold" data-testid="card-period-discount">−€{Math.round(pp.discount).toLocaleString("de-DE")}</span>}
                {!pp.min_nights_ok && <span className="block text-amber-700" data-testid="card-period-min-nights">{t.property.periodMinNights.replace("{n}", pp.min_nights)}</span>}
                {pp.min_nights_ok && <span> · {t.property.weekIncl}</span>}
              </span>
            : property.longterm_only
              ? <span className="font-semibold text-amber-800" data-testid="card-longterm-only">{t.property.longtermOnly.replace("{n}", site.longterm_min_days || 90)}</span>
              : <span><span className="font-semibold text-brand">{t.property.weekFrom.replace("{price}", weekFrom(property, site.extra_costs).toLocaleString("de-DE"))}</span> · {t.property.weekIncl}</span>}
          {row && (
            <Link to={url} className="inline-flex items-center gap-1.5 rounded-md bg-brand/75 text-white px-4 py-1.5 text-xs font-medium group-hover:bg-brand transition-colors shrink-0" data-testid={`card-open-${property.slug}`}>
              {t.wishlist.open}<ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
